import { GlobalSettings } from "../global_settings.js";
import { VersionLoader } from "../version_loader.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { AuthSessionStore } from "./auth_session.js";
import { NetworkManager } from "../network_manager.js";
import { ServerClock } from "../offline/server_clock.js";
import { TransportFlags } from "./transport_flags.js";
import { acceptFor, contentTypeFor, decodeResponseBody, encodeBody, type WireFormat } from "./transport_codec.js";
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";

const DEFAULT_TIMEOUT_MS = 10000;

export const getApiBaseUrl = (): string => GlobalSettings.API_BASE_URL;

export class ApiError extends Error
{
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  )
  {
    super( message );
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error
{
  constructor( message = 'Network request failed' )
  {
    super( message );
    this.name = 'NetworkError';
  }
}

let onUnauthorized: ( () => Promise<void> ) | null = null;
export function setOnUnauthorized( fn: () => Promise<void> ): void
{
  onUnauthorized = fn;
}

function url( path: string ): string
{
  return `${GlobalSettings.API_BASE_URL}/${path.replace( /^\/+/, '' )}`;
}

function wireFormat(): WireFormat
{
  return TransportFlags.current();
}

function formatHeaders( format: WireFormat ): Record<string, string>
{
  return {
    'content-type': contentTypeFor( format ),
    'accept': acceptFor( format ),
  };
}

async function fetchWithTimeout( input: string, init: RequestInit = {}, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<Response>
{
  if ( !NetworkManager.isOnline() && NetworkManager.isOfflinePlayable() )
    throw new NetworkError( 'offline' );

  const controller = new AbortController();
  const timeout_id = setTimeout( () => controller.abort(), timeout_ms );
  const started_at = performance.now();
  const method = init.method ?? 'GET';
  try
  {
    const res = await fetch( input, { ...init, signal: controller.signal } );
    ServerClock.noteResponseDate( res.headers.get( 'date' ) );
    NetworkManager.noteNetworkSuccess();
    CrashBreadcrumbs.http( method, input, res.status, performance.now() - started_at );
    return res;
  }
  catch ( e )
  {
    const error = new NetworkError( e instanceof Error ? e.message : String( e ) );
    CrashBreadcrumbs.http( method, input, error.message, performance.now() - started_at );
    NetworkManager.handleNetworkError( error );
    throw error;
  }
  finally
  {
    clearTimeout( timeout_id );
  }
}

async function readBody( res: Response ): Promise<unknown>
{
  try { return await decodeResponseBody( res ); }
  catch ( e )
  {
    console.warn( `[Api] could not decode response body (${res.status} ${res.headers.get( 'content-type' ) ?? 'no content-type'})`, e );
    return null;
  }
}

async function parseOrThrow<T = any>( res: Response ): Promise<T>
{
  const body = await readBody( res ) as any;
  if ( !res.ok )
  {
    throw new ApiError( res.status, body?.error ?? body?.message ?? `HTTP ${res.status}`, body );
  }
  return body as T;
}

export async function post<T = any>( path: string, data?: unknown, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<T>
{
  const format = wireFormat();
  const res = await fetchWithTimeout( url( path ), {
    method: 'POST',
    headers: formatHeaders( format ),
    body: data !== undefined ? encodeBody( data, format ) : undefined,
  }, timeout_ms );
  return parseOrThrow<T>( res );
}

export async function get<T = any>( path: string, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<T>
{
  const res = await fetchWithTimeout( url( path ), { headers: { accept: acceptFor( wireFormat() ) } }, timeout_ms );
  return parseOrThrow<T>( res );
}

export function detectPlatform(): string
{
  const capacitor_platform = ( window as any ).Capacitor?.getPlatform?.();
  if ( capacitor_platform && capacitor_platform !== 'web' )
    return capacitor_platform;
  if ( TelegramAPISettings.isTelegramEnvironment() )
    return 'telegram';
  // Telegram Web (webk/weba) is not in the native platform list, but it is still inside Telegram.
  const init_data = ( window as any )?.Telegram?.WebApp?.initData;
  if ( typeof init_data === 'string' && init_data.length > 0 )
    return 'telegram';
  return 'web';
}

function authHeaders( format: WireFormat ): Record<string, string>
{
  const token = AuthSessionStore.getToken();
  return {
    ...formatHeaders( format ),
    'x-app-version': VersionLoader.getVersion(),
    'x-platform': detectPlatform(),
    ...( token ? { authorization: `Bearer ${token}` } : {} ),
  };
}

async function authFetchResponse( path: string, init: RequestInit, timeout_ms = DEFAULT_TIMEOUT_MS, retried = false ): Promise<Response>
{
  const res = await fetchWithTimeout( url( path ), { ...init, headers: { ...authHeaders( wireFormat() ), ...( init.headers as Record<string, string> | undefined ) } }, timeout_ms );

  if ( res.status === 401 && !retried && onUnauthorized )
  {
    await onUnauthorized();
    return authFetchResponse( path, init, timeout_ms, true );
  }

  return res;
}

async function authFetch<T = any>( path: string, init: RequestInit, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<T>
{
  return parseOrThrow<T>( await authFetchResponse( path, init, timeout_ms ) );
}

function authSend<T = any>( path: string, method: 'POST' | 'PATCH', data: unknown, timeout_ms: number ): Promise<T>
{
  const format = wireFormat();
  return authFetch<T>( path, {
    method,
    headers: formatHeaders( format ),
    body: data !== undefined ? encodeBody( data, format ) : undefined,
  }, timeout_ms );
}

export interface ResponseWithMeta<T>
{
  data: T;
  etag: string | null;
}

export async function authGetWithMeta<T = any>( path: string, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<ResponseWithMeta<T>>
{
  const res = await authFetchResponse( path, { method: 'GET' }, timeout_ms );
  const data = await parseOrThrow<T>( res );
  return { data, etag: res.headers.get( 'etag' ) };
}

export type ConditionalResponse<T> =
  | { status: 200; data: T; etag: string | null }
  | { status: 304 };

export async function authGetConditional<T = any>( path: string, etag: string | null, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<ConditionalResponse<T>>
{
  const headers: Record<string, string> = etag ? { 'if-none-match': etag } : {};
  const res = await authFetchResponse( path, { method: 'GET', headers }, timeout_ms );
  if ( res.status === 304 )
    return { status: 304 };
  const data = await parseOrThrow<T>( res );
  return { status: 200, data, etag: res.headers.get( 'etag' ) };
}

export function authGet<T = any>( path: string, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<T>
{
  return authFetch<T>( path, { method: 'GET' }, timeout_ms );
}

export function authPost<T = any>( path: string, data?: unknown, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<T>
{
  return authSend<T>( path, 'POST', data, timeout_ms );
}

export function authPatch<T = any>( path: string, data?: unknown, timeout_ms = DEFAULT_TIMEOUT_MS ): Promise<T>
{
  return authSend<T>( path, 'PATCH', data, timeout_ms );
}
